import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Computes resource usage vs provider quota limits.
 * Counts instances, CPUs, and network across all active clusters
 * plus the current form's selections.
 */
export default function useQuotas(provider, form) {
    const providers = useSelector(({ providers }) => providers.providers.list);
    const clustersArr = useSelector(({ clusters }) => clusters.cluster.list);

    return useMemo(() => {
        if (!form?.regionId || !providers?.length) return null;
        const providerData = providers.find(p => p.id === provider);
        if (!providerData?.regions || !providerData?.instanceSizes) return null;

        const region = providerData.regions.find(r => r.region_id === form.regionId);
        const zone = region?.zones?.find(z => z.zone_id === form.zoneId || z.zone_id === form.regionId);
        if (!zone?.quotas) return null;

        const getCores = (instanceSizes, instanceId) => {
            const match = instanceSizes.find(s => s.instance_id === instanceId);
            return parseInt(match?.cores || 0, 10);
        };

        // Usage from existing active clusters
        const activeClusters = clustersArr.filter(c => c.info?.state !== 'down' && c.provider === provider);
        let usedInstances = activeClusters.length;
        let usedNetwork = activeClusters.length;
        let usedCpus = 0;

        activeClusters.forEach(c => {
            const fp = providers.find(p => p.id === c.provider);
            if (!fp?.instanceSizes) return;
            (c.data?.node?.group || []).forEach(g => {
                usedInstances += parseInt(g.count || 0, 10);
                usedCpus += getCores(fp.instanceSizes, g.instance_id) * parseInt(g.count || 0, 10);
            });
            usedCpus += getCores(fp.instanceSizes, c.data?.server?.instance_id);
        });

        // Usage from current form
        usedNetwork++;
        usedInstances++;
        usedCpus += getCores(providerData.instanceSizes, form.serverInstanceId);
        (form.nodeGroups || []).forEach(g => {
            usedInstances += parseInt(g.count || 0, 10);
            usedCpus += getCores(providerData.instanceSizes, g.instance_id) * parseInt(g.count || 0, 10);
        });

        const limits = {
            cpus: zone.quotas.cpus || 9999999,
            instances: zone.quotas.intance || 9999999,
            network: zone.quotas.network || 9999999,
        };

        return {
            usedCpus, usedInstances, usedNetwork,
            ...limits,
            exceeded: usedCpus > limits.cpus || usedInstances > limits.instances || usedNetwork > limits.network,
        };
    }, [provider, form?.regionId, form?.zoneId, form?.serverInstanceId, form?.nodeGroups, providers, clustersArr]);
}
